import clsx from "clsx";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import {
  HOME_COUNT_BADGE_CLASS,
  HOME_DROPDOWN_PANEL_CLASS,
  HOME_FILTER_BUTTON_CLASS,
} from "@/components/home/homeSurfaces";
import { Button } from "@/components/ui/button/Button";
import type { NeedsYouRiskFilter } from "./needsYouTableModel";

const RISK_OPTIONS: { value: NeedsYouRiskFilter; label: string }[] = [
  { value: "all", label: "All risks" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const OPTION_SELECTED =
  "bg-indigo-100 text-indigo-900 dark:bg-indigo-500/15 dark:text-indigo-100";

const OPTION_DEFAULT =
  "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800/80 dark:hover:text-slate-100";

export type HomeNeedsYouRiskFilterProps = {
  value: NeedsYouRiskFilter;
  onChange: (value: NeedsYouRiskFilter) => void;
  filteredCount: number;
  className?: string;
  testId?: string;
};

export function HomeNeedsYouRiskFilter({
  value,
  onChange,
  filteredCount,
  className,
  testId = "home-needs-you-risk-filter",
}: HomeNeedsYouRiskFilterProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  const updatePanelPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    setPanelStyle({
      position: "fixed",
      top: rect.bottom + 6,
      left: Math.max(8, rect.right - 160),
      width: 160,
      zIndex: 9999,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePanelPosition();
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;
    const handleReposition = () => updatePanelPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const showBadge = value !== "all";

  const panel =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={panelRef}
            style={panelStyle}
            className={clsx("p-1", HOME_DROPDOWN_PANEL_CLASS)}
            role="listbox"
            aria-label="Filter by risk"
            data-testid={`${testId}-panel`}
          >
            {RISK_OPTIONS.map((option) => (
              <Button
                key={option.value}
                type="button"
                plain
                size="unstyled"
                role="option"
                aria-selected={value === option.value}
                className={clsx(
                  "flex w-full rounded-lg px-2.5 py-2 text-left text-xs transition-colors",
                  value === option.value ? OPTION_SELECTED : OPTION_DEFAULT,
                )}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </Button>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={clsx("relative shrink-0", className)} data-testid={testId}>
      <Button
        ref={buttonRef}
        type="button"
        plain
        size="sm"
        className={HOME_FILTER_BUTTON_CLASS}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="listbox"
        title="Filter by risk"
        data-testid={`${testId}-button`}
      >
        <FontAwesomeIcon icon={faFilter} className="h-3.5 w-3.5" />
        {showBadge ? (
          <span
            className={clsx(
              "inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-xs font-medium tabular-nums",
              HOME_COUNT_BADGE_CLASS,
            )}
            data-testid={`${testId}-badge`}
          >
            {filteredCount}
          </span>
        ) : null}
      </Button>
      {panel}
    </div>
  );
}
